import { Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AssetType } from '@app/core/model/entities/asset/asset-type';
import { DatagridVisualisation } from '@app/core/model/entities/visualisation/datagrid-visualisation';
import { DatagridVisualisationService } from '@app/shared/services/datagrid-visualisation.service';
import { GridStateService } from '@app/shared/services/grid-state.service';
import { UserPreferencesService } from '@app/shared/services/user-preferences.service';
import { TranslateService } from '@ngx-translate/core';
import { IToolPanelAngularComp } from 'ag-grid-angular';
import { ColumnState, IToolPanelParams } from 'ag-grid-community';
import { Observable } from 'rxjs';
import { combineLatestWith, filter, map, mergeWith } from 'rxjs/operators';

@Component({
    selector: 'visualisation-tool-panel',
    templateUrl: './visualisation-tool-panel.component.html',
    styleUrls: ['./visualisation-tool-panel.component.scss'],
    standalone: false
})
export class VisualisationToolPanelComponent implements IToolPanelAngularComp, OnDestroy {

  protected visualisationForm: FormGroup<{
    currentVisualisation: FormControl<DatagridVisualisation>,
    currentAssetType?: FormControl<AssetType>
  }>;
  protected visualisations$: Observable<DatagridVisualisation[]>;

  protected preferenceIdKey: string;
  protected initialized = false;
  private originalColumnStates: ColumnState[];

  // Injection
  protected readonly datagridVisualisationService = inject(DatagridVisualisationService);
  protected readonly fb = inject(FormBuilder);
  protected readonly gridStateService = inject(GridStateService);
  protected readonly translate: TranslateService = inject(TranslateService);
  protected readonly userPreferencesService = inject(UserPreferencesService);
  protected readonly destroyRef = inject(DestroyRef);

  /**
   * Initializes the form
   */
  constructor() {
    this.visualisationForm = this.fb.group({
      currentVisualisation: this.fb.control(null, Validators.required)
    });
  }

  /**
   * Stop Observable subscriptions.
   * Clears data.
   */
  public ngOnDestroy(): void {
    this.datagridVisualisationService.clearData();
  }

  /**
   * Method used by Ag-grid to initialise the tool panel with grid parameter.
   * Initialises the tool panel with grid parameters.
   * @param params this is used to get the Grid API
   */
  public agInit(params: any): void {
    this.preferenceIdKey = params.preferenceIdKey;

    this.visualisationForm.controls.currentVisualisation.valueChanges
      .pipe(
        filter(() => this.initialized),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(datagridVisualisation => {
        this.userPreferencesService.currentUserPreferences.preferences[this.preferenceIdKey] = datagridVisualisation.id;
        this.userPreferencesService.updateUserPreferences();
        this.saveVisualisationAsGridState(datagridVisualisation);
        this.gridStateService.restoreGridState();
      });

    this.datagridVisualisationService.loadData()
      .pipe(
        combineLatestWith(this.gridStateService.gridReady$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([datagridVisualisations, originalColumnState]) => {
        this.originalColumnStates = originalColumnState;

        const defaultVisualisation = datagridVisualisations.find(viz => viz.isDefault);

        // get preferred visualisation id
        const preferredVizId = this.userPreferencesService.currentUserPreferences.preferences[this.preferenceIdKey];

        // get the visualisation of the user's preference
        // otherwise, get the default visualisation
        const currentVisualisation = datagridVisualisations.singleOrNull(viz => viz.id === preferredVizId) || defaultVisualisation;

        this.onDataLoaded(currentVisualisation, defaultVisualisation);
        this.initialized = true;
      });

    this.afterAgInit();
  }

  /**
   * Method is called after Ag-grid to initialise the tool panel with grid parameter.
   * It sets up subscriptions to handle changes in datagrid visualizations.
   */
  protected afterAgInit(): void {
    // Initialize or update the list of visualisations if one is created or updated
    this.visualisations$ = this.datagridVisualisationService.datagridVisualisationsLoaded$
      .pipe(
        mergeWith(this.datagridVisualisationService.applyDatagridVisualisation$),
        map(() => this.datagridVisualisationService.datagridVisualisations),
        takeUntilDestroyed(this.destroyRef)
      );

    this.datagridVisualisationService.applyDatagridVisualisation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(visualisation => {
        this.visualisationForm.controls.currentVisualisation.setValue(visualisation);
      });

    // Subscription to handle deletion of datagrid visualizations.
    this.datagridVisualisationService.deleteDatagridVisualisation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Once a visualisation is deleted, default back to the default visualisation
        this.visualisationForm.controls.currentVisualisation.setValue(
          this.datagridVisualisationService.datagridVisualisations.find(visualisation =>
            visualisation.isDefault
          )
        );
      });
  }

  /**
   * Open a dialog to create a visualisation.
   */
  public createVisualisation(): void {
    this.datagridVisualisationService.openCreateVisualisationDialog();
  }

  /**
   * Update the current visualisation.
   */
  public updateVisualisation(): void {
    const currentVisualisation = this.visualisationForm.controls.currentVisualisation.value;
    this.datagridVisualisationService.updateDatagridVisualisation(currentVisualisation, this.gridStateService.gridState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /**
   * Open a dialog to delete a visualisation.
   */
  public deleteVisualisation(): void {
    const currentVisualisation = this.visualisationForm.controls.currentVisualisation.value;
    this.datagridVisualisationService.openDeleteVisualisationDialog(currentVisualisation);
  }

  /**
   * Handles when all the visualisation data are loaded.
   * @param currentDatagridVisualisation - The current datagrid visualisation.
   * @param defaultDatagridVisualisation - The default datagrid visualisation.
   */
  public onDataLoaded(currentDatagridVisualisation: DatagridVisualisation,
                      defaultDatagridVisualisation: DatagridVisualisation): void {
    // Skip visualisation initialisation if config is missing
    if (!currentDatagridVisualisation?.config || !defaultDatagridVisualisation?.config) {
      console.error('Datagrid visualisation config is missing');
      return;
    }

    // Translate filter values
    if (defaultDatagridVisualisation.config.filterModel) {
      const filterModel = defaultDatagridVisualisation.config.filterModel;
      Object.keys(filterModel).forEach(key => {
        filterModel[key].values = filterModel[key].values.map((value: any) => typeof value === 'string' ? this.translate.instant(value) : value);
      });
    }

    // set the current visualisation
    this.visualisationForm.controls.currentVisualisation.setValue(currentDatagridVisualisation);

    // If there is no grid state in the session storage, save and apply the current visualisation
    if (!this.gridStateService.gridState) {
      this.saveVisualisationAsGridState(currentDatagridVisualisation);
      this.gridStateService.restoreGridState();
    }
  }

  /**
   * Save the DatagridVisualisation in the GridStateService. Column states from the visualisation are merged with
   * those from the grid's column definitions.
   * @param visualisation Visualisation to save.
   * @private
   */
  private saveVisualisationAsGridState(visualisation: DatagridVisualisation): void {
    // Visualisation may not contain states of every column in the datagrid, so we merge column states from
    // the visualisation with those from the datagrid.
    // Columns that are not present in the visualisation are hidden.
    const columnStatesToAdd = this.originalColumnStates.filter(colState =>
      !visualisation.config.columnState.find(({ colId }) => colId === colState.colId)
    )
      .map(colState => {
        colState.hide = true; // Update column state so the column is hidden
        return colState;
      });

    // Save column state in grid state
    this.gridStateService.gridState = {
      filterModel: visualisation.config.filterModel,
      columnState: [
        ...visualisation.config.columnState,
        ...columnStatesToAdd
      ],
      modified: false
    };
  }

  /**
   * Utility function to properly compare two datagrid visualisations by their IDs.
   * @param visualisation1 First visualisation to compare
   * @param visualisation2 Second visualisation to compare
   * @protected
   */
  protected compareById(visualisation1: DatagridVisualisation, visualisation2: DatagridVisualisation): boolean {
    return visualisation1 && visualisation2 && visualisation1.id === visualisation2.id;
  }

  /**
   * Indicates to AG Grid that the tool panel does not support in-place refresh.
   *
   * @param _ Parameters passed by AG Grid to the tool panel.
   * @return false to instruct AG Grid to recreate the tool panel rather than attempting a partial refresh.
   */
  public refresh(_?: IToolPanelParams): boolean | void {
    return false;
  };
}
